import { type ReactElement, useMemo } from 'react';
import { hasVisibleStackedChartValue } from '@/features/executionRuns/pages/executionRunsCharts';
import { getChartTitle } from '@/shared/copy/charts';
import { getErrorCopy } from '@/shared/copy/errors';
import { r, s, typographyTokens } from '@/theme/tokens';
import { uiTokens } from '@/theme/uiTokens';
import { StatusBanner } from '@/ui';
import { CHART_EMPTY_STATE_FILTER_HINT, ChartEmptyState, Panel } from '@/ui/composites';
import { Box, LineChartSkeleton, PieChart, Skeleton, Text, useTheme } from '@/ui/primitives';
import { InfoOutlinedIcon } from '@/ui/primitives/icons';
import { StackedTotalsTooltip } from '@/ui/primitives/StackedBarChartTooltip';
import { buildStackedBarChartTooltipSx } from '@/ui/primitives/stackedBarChartTooltipStyles';
import { MuiLineChart } from '@/ui/primitives/xCharts';
import { chartLoadErrorBannerSx } from './chartLoadErrorBannerSx';
import type { StatusPieDatum } from './StackedMetricsStatusChartSection.panelTypes';
import type { MetricsStatusChartPanelDisplayModel } from './useMetricsStatusChartPanelModel';

type StatusTrendPreviewPanelsProps = {
  loading: boolean;
  showChartErrorState: boolean;
  statusChartData: MetricsStatusChartPanelDisplayModel['statusChartData'];
  statusEmptyTitle: string;
  statusPieData: StatusPieDatum[];
};

type StatusPreviewTimelineLegendItem = {
  key: string;
  label: string;
  color?: string;
};

const previewPanelHeight = 184;
const previewChartHeight = 124;
const pieChartHeight = 116;
const previewPanelHeaderMinHeight = 20;
const statusPreviewLegendDotSize = 9;
const statusPreviewTimelineLegendLineWidth = 22;
const statusPreviewTimelineLegendLineHeight = 3;
const statusPreviewColours = {
  passed: '#22c55e',
  failed: '#ff4d57',
  skipped: '#f2b637',
  errored: '#c98255',
  untested: '#9aa4b2',
} as const;
const percentageFormatter = new Intl.NumberFormat('en-GB', {
  maximumFractionDigits: 1,
});
const shortMonthDayFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
});

const formatShare = (value: number, total: number): string => {
  if (total <= 0) return '0%';
  return `${percentageFormatter.format((value / total) * 100)}%`;
};

const resolvePreviewYAxisMax = (
  rows: Array<Record<string, string | number>>,
  series: Array<{ key: string }>,
): number => {
  const maxTotal = rows.reduce((currentMax, row) => {
    const rowTotal = series.reduce((sum, item) => {
      const value = row[item.key];
      return sum + (typeof value === 'number' && Number.isFinite(value) ? value : 0);
    }, 0);
    return rowTotal > currentMax ? rowTotal : currentMax;
  }, 0);
  if (maxTotal <= 0) return 1;

  const paddedMax = maxTotal * 1.12;
  const step = 100;
  return Math.ceil(paddedMax / step) * step;
};

const buildPreviewYAxisTicks = (max: number): number[] => {
  if (max <= 0) return [0, 100];
  const step = max / 4;
  return [0, step, step * 2, step * 3, max].map((value) => Math.round(value));
};

const formatShortMonthDay = (label: string): string => {
  const trimmed = label.trim();
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(trimmed);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return shortMonthDayFormatter.format(new Date(Number(year), Number(month) - 1, Number(day)));
  }

  const slashMatchWithYear = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(trimmed);
  if (slashMatchWithYear) {
    const [, day, month, year] = slashMatchWithYear;
    return shortMonthDayFormatter.format(new Date(Number(year), Number(month) - 1, Number(day)));
  }

  const slashMatch = /^(\d{1,2})\/(\d{1,2})$/.exec(trimmed);
  if (slashMatch) {
    const [, day, month] = slashMatch;
    const currentYear = new Date().getFullYear();
    return shortMonthDayFormatter.format(new Date(currentYear, Number(month) - 1, Number(day)));
  }

  return trimmed;
};

const resolveStatusPreviewColor = (statusKey: string): string => {
  if (statusKey === 'passed') return statusPreviewColours.passed;
  if (statusKey === 'failed') return statusPreviewColours.failed;
  if (statusKey === 'skipped') return statusPreviewColours.skipped;
  if (statusKey === 'errored') return statusPreviewColours.errored;
  if (statusKey === 'untested') return statusPreviewColours.untested;
  return uiTokens.statusColors.teal;
};

const StatusPreviewPanelTitle = ({ title }: { title: string }): ReactElement => (
  <Box
    component="div"
    sx={{
      m: s('none'),
      display: 'flex',
      alignItems: 'center',
      gap: s('sm'),
      color: (theme) => theme.app.colors.textMuted,
      fontSize: (muiTheme) => muiTheme.typography.subtitle1.fontSize,
      fontWeight: typographyTokens.overline.fontWeight,
      lineHeight: 1.25,
      whiteSpace: 'nowrap',
    }}
  >
    <Box component="span" sx={{ flexShrink: 0, minWidth: 'max-content' }}>
      {title}
    </Box>
    <InfoOutlinedIcon
      sx={{ flexShrink: 0, fontSize: 17, color: 'inherit', transform: 'translateY(1px)' }}
    />
  </Box>
);

const StatusPreviewPanelHeader = ({ title }: { title: string }): ReactElement => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: s('md'),
      minHeight: `${previewPanelHeaderMinHeight}px`,
      mb: s('md'),
    }}
  >
    <StatusPreviewPanelTitle title={title} />
  </Box>
);

const StatusPreviewErrorState = (): ReactElement => (
  <Box
    sx={{
      height: previewChartHeight,
      display: 'grid',
      placeItems: 'center',
    }}
  >
    <StatusBanner
      tone="red"
      variant="outlined"
      message={getErrorCopy('banner.chartLoadFailed')}
      sx={chartLoadErrorBannerSx}
    />
  </Box>
);

const StatusPreviewPieSkeleton = (): ReactElement => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: 'minmax(160px, 0.85fr) minmax(0, 1fr)',
      alignItems: 'center',
      gap: s('lg'),
      height: previewChartHeight,
    }}
  >
    <Box sx={{ display: 'grid', placeItems: 'center' }}>
      <Skeleton variant="circular" width={156} height={156} />
    </Box>
    <Box sx={{ display: 'grid', gap: s('md') }}>
      {[118, 104, 126, 96].map((width, index) => (
        <Skeleton
          key={`status-preview-pie-legend-${String(index)}`}
          variant="rounded"
          width={width}
          height={16}
          sx={{ borderRadius: r('md') }}
        />
      ))}
    </Box>
  </Box>
);

const StatusPreviewLegend = ({ data }: { data: StatusPieDatum[] }): ReactElement => {
  const theme = useTheme();
  const total = data.reduce((sum, item) => sum + item.value, 0);

  return (
    <Box sx={{ display: 'grid', gap: s('sm'), minWidth: 0 }}>
      {data.map((entry) => (
        <Box
          key={entry.id}
          sx={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) auto',
            alignItems: 'center',
            columnGap: s('lg'),
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: s('md'),
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                width: `${statusPreviewLegendDotSize}px`,
                height: `${statusPreviewLegendDotSize}px`,
                borderRadius: r('pill'),
                backgroundColor: entry.color ?? theme.app.colors.textMuted,
                flexShrink: 0,
              }}
            />
            <Text
              role="bodyMuted"
              sx={{
                minWidth: 0,
                fontSize: typographyTokens.body.fontSize,
                fontWeight: typographyTokens.caption.fontWeight,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {entry.name}
            </Text>
          </Box>
          <Text
            role="body"
            sx={{
              color: theme.app.colors.textMuted,
              fontSize: typographyTokens.body.fontSize,
              fontWeight: typographyTokens.caption.fontWeight,
              fontVariantNumeric: 'tabular-nums',
              whiteSpace: 'nowrap',
            }}
          >
            {`${entry.value} (${formatShare(entry.value, total)})`}
          </Text>
        </Box>
      ))}
    </Box>
  );
};

const StatusPreviewPiePanel = ({
  loading,
  showChartErrorState,
  statusEmptyTitle,
  statusPieData,
}: Pick<
  StatusTrendPreviewPanelsProps,
  'loading' | 'showChartErrorState' | 'statusEmptyTitle' | 'statusPieData'
>): ReactElement => {
  const previewPieData = useMemo(
    () =>
      statusPieData.map((item) => ({
        ...item,
        color: resolveStatusPreviewColor(item.id),
      })),
    [statusPieData],
  );
  const total = previewPieData.reduce((sum, item) => sum + item.value, 0);

  return (
    <Panel
      padding="none"
      gap="none"
      contentGap="none"
      sx={{
        minWidth: 0,
        minHeight: previewPanelHeight,
        backgroundColor: (theme) => `${theme.app.colors.surface} !important`,
      }}
    >
      <Box sx={{ px: s('lg'), pt: s('lg'), pb: s('lg'), boxSizing: 'border-box' }}>
        <StatusPreviewPanelHeader title={getChartTitle('statusBreakdown')} />
        {loading ? (
          <StatusPreviewPieSkeleton />
        ) : showChartErrorState ? (
          <StatusPreviewErrorState />
        ) : previewPieData.length > 0 ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'minmax(0, 1fr)',
                sm: 'minmax(150px, 0.75fr) minmax(0, 1.25fr)',
              },
              alignItems: 'center',
              gap: s('lg'),
              minHeight: previewChartHeight,
            }}
          >
            <Box sx={{ position: 'relative', minWidth: 0 }}>
              <PieChart
                data={previewPieData}
                height={pieChartHeight}
                innerRadius={34}
                outerRadius="90%"
                showLegend={false}
              />
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'grid',
                  placeItems: 'center',
                  pointerEvents: 'none',
                }}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Text
                    role="body"
                    sx={{
                      fontSize: (muiTheme) => muiTheme.typography.h3.fontSize,
                      lineHeight: 1,
                      fontWeight: typographyTokens.overline.fontWeight,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {total}
                  </Text>
                  <Text
                    role="caption"
                    sx={{ color: 'text.secondary', fontSize: typographyTokens.body.fontSize }}
                  >
                    Runs
                  </Text>
                </Box>
              </Box>
            </Box>
            <StatusPreviewLegend data={previewPieData} />
          </Box>
        ) : (
          <Box sx={{ height: previewChartHeight, display: 'grid', placeItems: 'center' }}>
            <ChartEmptyState title={statusEmptyTitle} body={CHART_EMPTY_STATE_FILTER_HINT} />
          </Box>
        )}
      </Box>
    </Panel>
  );
};

const StatusPreviewTimelineLegend = ({
  series,
}: {
  series: StatusPreviewTimelineLegendItem[];
}): ReactElement => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        columnGap: s('xl'),
        rowGap: s('xs'),
        minWidth: 0,
      }}
    >
      {series.map((item) => (
        <Box
          key={item.key}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: s('md'),
            color: theme.app.colors.textMuted,
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              width: `${statusPreviewTimelineLegendLineWidth}px`,
              height: `${statusPreviewTimelineLegendLineHeight}px`,
              borderRadius: r('pill'),
              backgroundColor: item.color ?? theme.app.colors.textMuted,
              flexShrink: 0,
            }}
          />
          <Text
            role="body"
            sx={{
              color: theme.app.colors.textMuted,
              fontSize: typographyTokens.body.fontSize,
              fontWeight: typographyTokens.caption.fontWeight,
            }}
          >
            {item.label}
          </Text>
        </Box>
      ))}
    </Box>
  );
};

const StatusPreviewTimelinePanel = ({
  loading,
  showChartErrorState,
  statusChartData,
  statusEmptyTitle,
}: Pick<
  StatusTrendPreviewPanelsProps,
  'loading' | 'showChartErrorState' | 'statusChartData' | 'statusEmptyTitle'
>): ReactElement => {
  const theme = useTheme();
  const hasData =
    statusChartData !== null &&
    hasVisibleStackedChartValue(statusChartData.data, statusChartData.series);
  const visibleStatusSeries = useMemo(
    () =>
      (statusChartData?.series ?? []).filter((item) =>
        (statusChartData?.data ?? []).some((row) => {
          const value = row[item.key];
          return typeof value === 'number' && Number.isFinite(value) && value > 0;
        }),
      ),
    [statusChartData?.data, statusChartData?.series],
  );
  const lineSeries = useMemo(
    () =>
      visibleStatusSeries.map((item) => ({
        key: item.key,
        label: item.label,
        color: resolveStatusPreviewColor(item.key),
      })),
    [visibleStatusSeries],
  );
  const timelineLegendItems = useMemo<StatusPreviewTimelineLegendItem[]>(() => {
    return lineSeries;
  }, [lineSeries]);
  const xLabels = useMemo(
    () => (statusChartData?.data ?? []).map((row) => String(row.label)),
    [statusChartData?.data],
  );
  const yAxisMax = useMemo(
    () => resolvePreviewYAxisMax(statusChartData?.data ?? [], lineSeries),
    [lineSeries, statusChartData?.data],
  );
  const yAxisTicks = useMemo(() => buildPreviewYAxisTicks(yAxisMax), [yAxisMax]);
  const areaSeries = useMemo(
    () =>
      lineSeries.map((item) => ({
        id: item.key,
        label: item.label,
        color: item.color,
        data: (statusChartData?.data ?? []).map((row) => {
          const value = row[item.key];
          return typeof value === 'number' && Number.isFinite(value) ? value : 0;
        }),
        area: true,
        stack: 'runs',
        showMark: false,
        valueFormatter: (value: number | null) =>
          typeof value === 'number' && Number.isFinite(value) ? String(value) : '',
      })),
    [lineSeries, statusChartData?.data],
  );

  return (
    <Panel
      padding="none"
      gap="none"
      contentGap="none"
      sx={{
        minWidth: 0,
        height: previewPanelHeight,
        backgroundColor: `${theme.app.colors.surface} !important`,
      }}
    >
      <Box
        sx={{
          height: '100%',
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          px: s('lg'),
          pt: s('lg'),
          pb: s('lg'),
          boxSizing: 'border-box',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            columnGap: s('lg'),
            minHeight: `${previewPanelHeaderMinHeight}px`,
            mb: s('md'),
          }}
        >
          <StatusPreviewPanelTitle title="Runs Over Time" />
          {timelineLegendItems.length > 0 ? (
            <StatusPreviewTimelineLegend series={timelineLegendItems} />
          ) : null}
        </Box>
        {loading ? (
          <LineChartSkeleton
            height={previewChartHeight}
            leftInset={`calc(${s('3xl')} + ${s('md')})`}
            topInset={s('sm')}
            hideYAxisLine
          />
        ) : showChartErrorState ? (
          <StatusPreviewErrorState />
        ) : hasData ? (
          <Box sx={{ minWidth: 0, height: previewChartHeight }}>
            <MuiLineChart
              series={areaSeries}
              xAxis={[
                {
                  data: xLabels,
                  scaleType: 'point',
                  valueFormatter: (value) => formatShortMonthDay(String(value)),
                  tickSize: 0,
                  tickLabelStyle: {
                    fill: theme.app.colors.textMuted,
                    fontSize: 13,
                    transform: 'translateY(10px)',
                  },
                },
              ]}
              yAxis={[
                {
                  min: 0,
                  max: yAxisMax,
                  width: 34,
                  tickSize: 0,
                  tickInterval: yAxisTicks,
                  valueFormatter: (value: number | null) =>
                    typeof value === 'number' && Number.isFinite(value) ? String(value) : '',
                  tickLabelStyle: {
                    fill: theme.app.colors.textMuted,
                    fontSize: 13,
                    transform: 'translateX(-4px)',
                  },
                },
              ]}
              height={previewChartHeight}
              margin={{ left: 0, right: 0, top: 0, bottom: 0 }}
              grid={{ horizontal: false, vertical: false }}
              slots={{
                tooltip: (tooltipProps) => (
                  <StackedTotalsTooltip
                    {...tooltipProps}
                    totalsByLabel={new Map()}
                    tooltipLabelFormatter={formatShortMonthDay}
                    tooltipEmptyValueLabel="No runs"
                  />
                ),
              }}
              slotProps={{
                tooltip: {
                  trigger: 'axis',
                  classes: {
                    paper: 'flowqa-stacked-tooltip-paper',
                    table: 'flowqa-stacked-tooltip-table',
                    cell: 'flowqa-stacked-tooltip-cell',
                    mark: 'flowqa-stacked-tooltip-mark',
                  },
                  sx: buildStackedBarChartTooltipSx({
                    borderColor: theme.app.colors.border,
                    textColor: theme.app.colors.text,
                    tooltipFontSize: typographyTokens.body.fontSize,
                    tooltipFontWeight: theme.typography.fontWeightRegular ?? 400,
                    tooltipLineHeight: theme.typography.body2.lineHeight ?? 1.4,
                  }),
                },
              }}
              sx={{
                '& .MuiChartsLegend-root': { display: 'none' },
                '& .MuiChartsAxis-line, & .MuiChartsAxis-tick': {
                  stroke: 'transparent !important',
                },
                '& .MuiChartsGrid-line': {
                  stroke: 'transparent !important',
                },
                '& .MuiAreaElement-root': {
                  fillOpacity: 0.28,
                },
                '& .MuiLineElement-root': {
                  strokeWidth: 2.2,
                },
                '& .MuiMarkElement-root': {
                  display: 'none',
                },
              }}
            />
          </Box>
        ) : (
          <Box sx={{ flex: '1 1 auto', minHeight: 0, display: 'grid', placeItems: 'center' }}>
            <ChartEmptyState title={statusEmptyTitle} body={CHART_EMPTY_STATE_FILTER_HINT} />
          </Box>
        )}
      </Box>
    </Panel>
  );
};

export const StatusTrendPreviewPanels = ({
  loading,
  showChartErrorState,
  statusChartData,
  statusEmptyTitle,
  statusPieData,
}: StatusTrendPreviewPanelsProps): ReactElement => (
  <Box
    sx={{
      width: '100%',
      display: 'grid',
      gridTemplateColumns: {
        xs: 'minmax(0, 1fr)',
        lg: 'minmax(280px, 0.7fr) minmax(0, 2.3fr)',
      },
      gap: s('xl'),
      alignItems: 'stretch',
    }}
  >
    <StatusPreviewPiePanel
      loading={loading}
      showChartErrorState={showChartErrorState}
      statusEmptyTitle={statusEmptyTitle}
      statusPieData={statusPieData}
    />
    <StatusPreviewTimelinePanel
      loading={loading}
      showChartErrorState={showChartErrorState}
      statusChartData={statusChartData}
      statusEmptyTitle={statusEmptyTitle}
    />
  </Box>
);
